import { Routes } from '@angular/router';
import { AbcAnalysisEmptyPageComponent } from './modules/abc-analysis/screens/abc-analysis-empty-page/abc-analysis-empty-page-screen.component';
import { HomeScreenComponent } from './screens/home-screen/home-screen.component';
import { LoginScreenComponent } from './screens/login-screen/login-screen/login-screen.component';
import { AuthGuardStandalone } from './shared/guards/auth.standalone.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { FeatureGuard } from './shared/guards/feature/feature.guard';
import { PermissionGuard } from './shared/guards/permission/permission.guard';
import { AuthCallbackComponent } from './screens/auth-callback/auth-callback.component';
import { LoginRedirectGuard } from './shared/guards/login-redirect.guard';
import {
  FEATURE_FLAG_ABC_ANALYSIS,
  FEATURE_FLAG_ACCESS_CONTROL,
  FEATURE_FLAG_AGENT_LIBRARY,
  FEATURE_FLAG_CONSTRAINED_SERVICE_LEVEL_OPTIMIZATION,
  FEATURE_FLAG_DEMAND_FORECASTING,
  FEATURE_FLAG_DEMAND_PREDICTION,
  FEATURE_FLAG_DEO_AGENT,
  FEATURE_FLAG_LEAD_TIME_PREDICTION,
  FEATURE_FLAG_OPTIMAL_REPLENISHMENT_QUANTITY,
  FEATURE_FLAG_SUPPLY_VARIABILITY,
  FEATURE_FLAG_DEFAULT_SETTINGS
} from './shared/services/feature-flags/feature-flags.constants';
import { HomeGuard } from './shared/guards/home/home.guard';
import { BlankScreenComponent } from './screens/blank-screen/blank-screen.component';
import { NotFoundScreenComponent } from './shared/screens/error-pages/not-found-screen/not-found-screen.component';
import { ErrorScreenComponent } from './shared/screens/error-pages/error-screen/error-screen.component';
import { NoAccessScreenComponent } from './shared/screens/error-pages/no-access-screen/no-access-screen.component';
import { PERMISSION_ROLE_MANAGE } from './shared/services/permissions/permissions.constants';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    component: LoginScreenComponent,
    canActivate: [LoginRedirectGuard]
  },
  { path: 'auth-callback', component: AuthCallbackComponent },
  {
    path: 'home',
    canActivate: [AuthGuardStandalone, FeatureGuard, HomeGuard],
    component: HomeScreenComponent
  },
  {
    path: 'start',
    canActivate: [AuthGuardStandalone],
    component: BlankScreenComponent
  },
  {
    path: 'abc-analysis',
    data: { breadcrumb: 'ABC Analysis', featureFlag: FEATURE_FLAG_ABC_ANALYSIS },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/abc-analysis/abc-analysis.module').then(m => m.AbcAnalysisModule)
  },
  {
    path: 'ltp',
    data: { breadcrumb: 'Lead Time Prediction', featureFlag: FEATURE_FLAG_LEAD_TIME_PREDICTION },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/ltp/ltp.module').then(m => m.LtpModule)
  },
  {
    path: 'demand-forecasting',
    data: { breadcrumb: 'Demand Forecasting', featureFlag: FEATURE_FLAG_DEMAND_FORECASTING },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/forecast/forecast.module').then(m => m.ForecastModule)
  },
  {
    path: 'demand-prediction',
    data: { breadcrumb: 'Demand Prediction', featureFlag: FEATURE_FLAG_DEMAND_PREDICTION },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/prediction/demand-prediction.module').then(m => m.DemandPredictionModule)
  },
  {
    path: 'abc-empty-page',
    component: AbcAnalysisEmptyPageComponent,
    canActivate: [AuthGuard, FeatureGuard],
    children: [{ path: 'abc-empty-page', canActivate: [], component: AbcAnalysisEmptyPageComponent }]
  },
  {
    path: 'components',
    data: { breadcrumb: 'Components' },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/components-showcase/components-showcase.module').then(m => m.ComponentsShowcaseModule)
  },
  {
    path: 'cslo',
    data: { breadcrumb: 'CSLO', featureFlag: FEATURE_FLAG_CONSTRAINED_SERVICE_LEVEL_OPTIMIZATION },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/cslo/cslo.module').then(m => m.CSLOModule)
  },
  {
    path: 'orq-ss',
    data: { breadcrumb: 'ORQ-SS', featureFlag: FEATURE_FLAG_OPTIMAL_REPLENISHMENT_QUANTITY },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/orq-ss/orq-ss.module').then(m => m.OrqSsModule)
  },
  {
    path: 'supply-variability',
    data: { breadcrumb: 'Supply Variability', featureFlag: FEATURE_FLAG_SUPPLY_VARIABILITY },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/supply-variability/supply-variability.module').then(m => m.SupplyVariabilityModule)
  },
  {
    path: 'settings',
    data: { breadcrumb: 'Settings', featureFlag: FEATURE_FLAG_DEFAULT_SETTINGS },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule)
  },
  {
    path: 'access-control',
    data: { breadcrumb: 'Access Control', featureFlag: FEATURE_FLAG_ACCESS_CONTROL, permission: PERMISSION_ROLE_MANAGE },
    canActivate: [AuthGuard, FeatureGuard, PermissionGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/access-control/access-control.module').then(m => m.AccessControlModule)
  },
  {
    path: 'agent-library',
    data: { breadcrumb: 'Agent Library', featureFlag: FEATURE_FLAG_AGENT_LIBRARY },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./modules/agent-library/agent-library.module').then(m => m.AgentLibraryModule)
  },
  {
    path: 'deo-ai-assist',
    data: { breadcrumb: 'DEO AI Assist', featureFlag: FEATURE_FLAG_DEO_AGENT },
    canActivate: [AuthGuard, FeatureGuard],
    canActivateChild: [AuthGuard],
    loadChildren: () => import('./shared/screens/deo-ai-assistant/deo-ai-assistant.module').then(m => m.AiAgentModule)
  },
  {
    path: 'error',
    data: { breadcrumb: 'Error' },
    component: ErrorScreenComponent
  },
  {
    path: 'no-access',
    data: { breadcrumb: 'No access' },
    canActivate: [AuthGuardStandalone],
    component: NoAccessScreenComponent
  },
  {
    path: 'not-found',
    data: { breadcrumb: 'Page not found' },
    component: NotFoundScreenComponent
  },
  {
    path: '**',
    component: NotFoundScreenComponent
  }
];
